import { create } from 'zustand';

const createId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;


// ─── Types ────────────────────────────────────────────────────────────────────

export type DeviceMode = 'desktop' | 'mobile';

export type BasicBlockType =
  | 'headerText'
  | 'fullWidthText'
  | 'twoColumnText'
  | 'textImage'
  | 'imageText'
  | 'fullWidthImage'
  | 'twoColumnImages'
  | 'normalButton'
  | 'separator';

export type SmartButtonType =
  | 'smartBtn_viewTreatmentPlan'
  | 'smartBtn_checkInsuranceEligibility'
  | 'smartBtn_payOnlineBalance'
  | 'smartBtn_bookAppointment'
  | 'smartBtn_scheduleCleaning'
  | 'smartBtn_emergencyCall'
  | 'smartBtn_claimOffer'
  | 'smartBtn_activateInvisalign'
  | 'smartBtn_completeIntakeForm'
  | 'smartBtn_leaveReview'
  | 'smartBtn_referFriend';

export type SmartBlockType =
  | 'beforeAfterSlider'
  | 'experienceInsurance'
  | 'financingOptions'
  | 'serviceSpotlight'
  | 'testimonial'
  | 'video'
  | SmartButtonType;

/** Singleton structural blocks — only one of each allowed, always pinned */
export type StructuralBlockType = 'emailHeader' | 'emailFooter';

export type HeaderLayoutStyle = 'stackedCenter' | 'logoTopTitleSubRow' | 'logoLeftTitleRight' | 'logoRightTitleLeft';

/** 'light' = light variant of theme, 'dark' = dark variant, 'custom' = only available when theme mode is custom */
export type BlockColorScheme = 'light' | 'dark' | 'custom';

export interface EmailHeaderContent {
  style: HeaderLayoutStyle;
  title: string;
  subtitle: string;
  logoUrl: string;
  colorScheme: BlockColorScheme;
  customBg: string;
  customText: string;
}

export const DEFAULT_HEADER_CONTENT: EmailHeaderContent = {
  style: 'stackedCenter',
  title: 'Company Name',
  subtitle: 'Your tagline goes here',
  logoUrl: '',
  colorScheme: 'light',
  customBg: '',
  customText: '',
};

// ─── Footer types ─────────────────────────────────────────────────────────────

export interface FooterLinkItem {
  text: string;
  url: string;
}

export interface FooterSocialItem {
  platform: 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'youtube' | 'tiktok' | 'pinterest' | 'github';
  url: string;
}

export interface EmailFooterContent {
  leftItems: [FooterLinkItem, FooterLinkItem, FooterLinkItem];
  rightItems: [FooterLinkItem, FooterLinkItem, FooterLinkItem];
  unsubscribeText: string;
  unsubscribeUrl: string;
  privacyText: string;
  privacyUrl: string;
  socials: FooterSocialItem[];
  colorScheme: BlockColorScheme;
  customBg: string;
  customText: string;
}

export const DEFAULT_FOOTER_CONTENT: EmailFooterContent = {
  leftItems: [
    { text: '', url: '' },
    { text: '', url: '' },
    { text: '', url: '' },
  ],
  rightItems: [
    { text: '', url: '' },
    { text: '', url: '' },
    { text: '', url: '' },
  ],
  unsubscribeText: 'Unsubscribe',
  unsubscribeUrl: '',
  privacyText: 'Privacy Policy',
  privacyUrl: '',
  socials: [],
  colorScheme: 'dark',
  customBg: '',
  customText: '',
};

// ─── Text / Image settings ────────────────────────────────────────────────────

export type HeadingLevel = 1 | 2 | 3 | 4;

export type LinkType = 'web' | 'onlineBooking' | 'insuranceCalculator' | 'treatmentPlan';

export interface TextSettings {
  /** Width of the text column as a percentage of its container (50–100) */
  widthPct: number;
  /** Font family name */
  fontFamily: string;
  /** Font size in px (for body text blocks only) */
  fontSize: number;
  /** Heading level (for headerText blocks only) */
  headingLevel: HeadingLevel;
  /** Full-block hyperlink URL */
  linkUrl: string;
  /** Link destination type */
  linkType: LinkType;
  /** Selected treatment plan id (only used when linkType === 'treatmentPlan') */
  treatmentPlanId?: string | undefined;
  /** Whether the link opens in a new tab */
  openInNewTab: boolean;
}

export const DEFAULT_TEXT_SETTINGS: TextSettings = {
  widthPct: 100,
  fontFamily: 'Inter, sans-serif',
  fontSize: 15,
  headingLevel: 1,
  linkUrl: '',
  linkType: 'web',
  treatmentPlanId: undefined,
  openInNewTab: false,
};

export const HEADING_FONT_SIZES: Record<number, number> = {
  1: 36,
  2: 28,
  3: 22,
  4: 18,
};

export type ImageSizeMode = 'fit' | 'original' | 'scale';

export interface ImageSettings {
  /** Size mode */
  sizeMode: ImageSizeMode;
  /** Scale percentage when sizeMode === 'scale' (30–100) */
  scalePct: number | undefined;
  /** Crop data as a CSS object-position string (set after cropping) */
  cropData?: string;
  /**
   * null = inherit from theme image shape.
   * Applies border-radius to the image element.
   */
  shape?: ButtonShape | null;
}

export const DEFAULT_IMAGE_SETTINGS: ImageSettings = {
  sizeMode: 'fit',
  scalePct: 100,
  shape: null,
};

// ─── Block content types ──────────────────────────────────────────────────────

export interface TextBlockContent {
  html: string;
  settings: TextSettings;
  title?: string;
  /** AI mode fields — only used when contentMode === 'ai' */
  contentMode?: 'normal' | 'ai';
  aiPrompt?: string;
  aiLanguage?: string;
}

export interface ImageBlockContent {
  src: string;
  alt: string;
  linkUrl?: string;
  linkType?: LinkType;
  treatmentPlanId?: string;
  openInNewTab?: boolean;
  settings: ImageSettings;
}

export interface TwoColumnTextContent {
  leftHtml: string;
  leftSettings: TextSettings;
  rightHtml: string;
  rightSettings: TextSettings;
  title?: string;
}

export type TextImageLayout = 'fit' | 'noGap';

export interface TextImageContent {
  layoutMode: TextImageLayout;
  textHtml: string;
  textSettings: TextSettings;
  image: Omit<ImageBlockContent, 'settings'> & { settings: ImageSettings };
  title?: string;
}

export interface ImageTextContent {
  layoutMode: TextImageLayout;
  image: Omit<ImageBlockContent, 'settings'> & { settings: ImageSettings };
  textHtml: string;
  textSettings: TextSettings;
  title?: string;
}

export type TwoColImageLayout = 'fit' | 'original' | 'noGap';

export interface TwoColumnImagesContent {
  layoutMode: TwoColImageLayout;
  leftImage: Omit<ImageBlockContent, 'settings'> & { settings: ImageSettings };
  rightImage: Omit<ImageBlockContent, 'settings'> & { settings: ImageSettings };
}

// ─── Button shared types ──────────────────────────────────────────────────────

export type ButtonShape = 'square' | 'rounded' | 'pill';
export type ButtonBorderStyle =
  | 'none'
  | 'solid'
  | 'dashed'
  | 'dotted'
  | 'double'
  | 'inset'
  | 'outset'
  | 'groove'
  | 'ridge';

export interface ButtonStyle {
  /**
   * null = inherit from theme.
   * When null, the rendered button uses the theme's button shape.
   */
  shape: ButtonShape | null;
  borderStyle: ButtonBorderStyle;
  borderWidth: number;
  borderColor: string;
  /**
   * null = inherit from theme.
   * When null, the rendered button uses the theme's buttonBackground color.
   */
  buttonColor: string | null;
  /**
   * null = inherit from theme.
   * When null, the rendered button uses the theme's buttonText color.
   */
  textColor: string | null;
  blockBackground: string;
}

export const DEFAULT_BUTTON_STYLE: ButtonStyle = {
  shape: null,
  borderStyle: 'none',
  borderWidth: 2,
  borderColor: '#2563eb',
  buttonColor: null,
  textColor: null,
  blockBackground: 'transparent',
};

// ─── Normal button block ──────────────────────────────────────────────────────

export interface NormalButtonContent {
  text: string;
  fontFamily: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  linkType: LinkType;
  treatmentPlanId?: string;
  linkUrl: string;
  openInNewTab: boolean;
  style: ButtonStyle;
}

export const DEFAULT_NORMAL_BUTTON_CONTENT: NormalButtonContent = {
  text: 'Click Here',
  fontFamily: 'Arial, sans-serif',
  fontSize: 14,
  bold: false,
  italic: false,
  underline: false,
  linkType: 'web',
  linkUrl: '',
  openInNewTab: false,
  style: { ...DEFAULT_BUTTON_STYLE },
};

/** Helper: resolve a nullable button color to a concrete value using theme fallback */
export const resolveButtonColor = (
  blockColor: string | null,
  themeColor: string,
): string => blockColor ?? themeColor;

/** Helper: resolve a nullable button shape to a concrete value using theme fallback */
export const resolveButtonShape = (
  blockShape: ButtonShape | null,
  themeShape: ButtonShape,
): ButtonShape => blockShape ?? themeShape;

/** Helper: resolve a nullable image shape to a concrete value using theme fallback */
export const resolveImageShape = (
  blockShape: ButtonShape | null | undefined,
  themeShape: ButtonShape,
): ButtonShape => blockShape ?? themeShape;

// ─── Smart button block ───────────────────────────────────────────────────────
// Each smart button type is its own block. Text and URL come from the backend;
// only the visual style is editable by the user.

export interface SmartButtonContent {
  style: ButtonStyle;
}

// ─── Smart block content types ────────────────────────────────────────────────

export interface GifCropSettings {
  /** Zoom level 1.0–3.0 */
  zoom: number;
  /** Horizontal pan offset in px (positive = pan right) */
  offsetX: number;
  /** Vertical pan offset in px (positive = pan down) */
  offsetY: number;
}

export interface BeforeAfterSliderContent {
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
  /** 0–100, position of the divider */
  sliderPosition: number;
  /** Height of the output GIF / preview in px (100–600) */
  frameHeight: number;
  /** Crop/zoom settings for the before image */
  beforeCrop: GifCropSettings;
  /** Crop/zoom settings for the after image */
  afterCrop: GifCropSettings;
  /** Generated GIF data URL (set after user clicks "Create GIF") */
  gifUrl?: string | undefined;
}

export interface ExperienceInsuranceContent {
  /** Image on the left — comes from backend, editable via file manager */
  image: string;
  imageAlt: string;
  /** Right side text — comes from backend */
  title: string;
  description: string;
  buttonLabel: string;
  buttonUrl: string;
}

export interface FinancingOptionsContent {
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  buttonLabel: string;
  buttonUrl: string;
  /** Shown as a read-only badge: data is filled automatically from backend */
  autoFillNote: string;
}

export interface ServiceSpotlightContent {
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  buttonLabel: string;
  buttonUrl: string;
}

export interface TestimonialItem {
  name: string;
  text: string;
  rating: number;
  avatarUrl: string;
}

export interface TestimonialContent {
  /** 1–3 */
  count: 1 | 2 | 3;
  items: [TestimonialItem, TestimonialItem, TestimonialItem];
}

export interface VideoContent {
  videoUrl: string;
  posterUrl: string;
  caption: string;
}

export type SmartBlockContent =
  | BeforeAfterSliderContent
  | ExperienceInsuranceContent
  | FinancingOptionsContent
  | ServiceSpotlightContent
  | TestimonialContent
  | VideoContent
  | SmartButtonContent;

// ─── Separator block ──────────────────────────────────────────────────────────

export type SeparatorWidthMode = 'full' | 'original' | 'custom';
export type SeparatorLineStyle = 'solid' | 'dashed' | 'dotted';

export interface SeparatorBlockContent {
  /** Vertical padding above and below the separator (2–150 px) */
  spacingPx: number | undefined;
  /** Whether to show a visible divider line */
  showLine: boolean;
  /** Width mode: full = edge-to-edge, original = page text padding, custom = custom % */
  widthMode: SeparatorWidthMode;
  /** Custom width as a percentage of the container (10–100), used when widthMode === 'custom' */
  widthPct: number | undefined;
  /** Line style */
  lineStyle: SeparatorLineStyle;
  /** Line thickness in px (1–8) */
  lineThickness: number | undefined;
  /** Override line color; empty string = use theme textColor at 20% opacity */
  lineColor: string;
}

export const DEFAULT_SEPARATOR_CONTENT: SeparatorBlockContent = {
  spacingPx: 24,
  showLine: true,
  widthMode: 'original',
  widthPct: 100,
  lineStyle: 'solid',
  lineThickness: 1,
  lineColor: '',
};

export type EmailBlockContent =
  | TextBlockContent
  | ImageBlockContent
  | TwoColumnTextContent
  | TextImageContent
  | ImageTextContent
  | TwoColumnImagesContent
  | NormalButtonContent
  | SeparatorBlockContent
  | SmartBlockContent;

export interface EmailBlock {
  id: string;
  type: BasicBlockType | SmartBlockType;
  content: EmailBlockContent;
  /** Custom CSS class name(s) added by the user via advanced mode */
  customClass?: string;
}

/** Structural singleton blocks (header / footer) */
export interface StructuralBlock {
  id: string;
  type: StructuralBlockType;
  headerContent?: EmailHeaderContent;
  footerContent?: EmailFooterContent;
}

export interface SavedEmailMeta {
  id: string;
  name: string;
  createdAt: string;
  html: string;
  status: 'draft' | 'sent';
}

export interface EmailSetupMeta {
  name: string;
  description: string;
  tags: string[];
}

// ─── Default content per block type ──────────────────────────────────────────

export const createDefaultSmartContent = (type: SmartBlockType): SmartBlockContent => {
  switch (type) {
    case 'beforeAfterSlider':
      return {
        beforeImage: '',
        beforeAlt: 'Before',
        afterImage: '',
        afterAlt: 'After',
        sliderPosition: 50,
        frameHeight: 300,
        beforeCrop: { zoom: 1, offsetX: 0, offsetY: 0 },
        afterCrop: { zoom: 1, offsetX: 0, offsetY: 0 },
        gifUrl: undefined,
      };
    case 'experienceInsurance':
      return {
        image: '/Insurance-reminder.png',
        imageAlt: 'Insurance reminder',
        title: 'Insurance reminder',
        description: 'Your plan renews soon, this is a good time to schedule a cleaning or check-up. We can help you confirm what may be covered.',
        buttonLabel: 'Schedule My Cleaning',
        buttonUrl: '#',
      };
    case 'financingOptions':
      return {
        image: '/Financing-options-available.png',
        imageAlt: 'Financing options available',
        title: 'Financing options available',
        description: "Need flexibility? We offer payment options to help make care more manageable. Ask our team what's available for your visit.",
        buttonLabel: 'View Options',
        buttonUrl: '#',
        autoFillNote: 'Financing details are automatically populated from your account settings.',
      };
    case 'serviceSpotlight':
      return {
        image: '/Ready-for-a-brighter-smile.png',
        imageAlt: 'Ready for a brighter smile',
        title: 'Ready for a brighter smile?',
        description: "Brighten your smile with professional whitening options designed for comfort and noticeable results. Ask us what's best for your goals and sensitivity level.",
        buttonLabel: 'Schedule My Whitening',
        buttonUrl: '#',
      };
    case 'testimonial':
      return {
        count: 1,
        items: [
          {
            name: 'Sarah M.',
            text: 'Absolutely incredible service! My home has never been this clean. The team was professional, thorough, and so friendly.',
            rating: 5,
            avatarUrl: 'https://i.pravatar.cc/48?img=47',
          },
          {
            name: 'James T.',
            text: "I've tried many cleaning services but Elva is by far the best. They pay attention to every detail and always go above and beyond.",
            rating: 5,
            avatarUrl: 'https://i.pravatar.cc/48?img=12',
          },
          {
            name: 'Linda K.',
            text: 'Reliable, trustworthy, and they do an amazing job every single time. Highly recommend to anyone looking for a premium clean.',
            rating: 5,
            avatarUrl: 'https://i.pravatar.cc/48?img=32',
          },
        ],
      };
    case 'video':
      return {
        videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
        posterUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
        caption: 'Watch how we transform your home',
      };
    // All 11 smart button types share the same default style-only content
    case 'smartBtn_viewTreatmentPlan':
    case 'smartBtn_checkInsuranceEligibility':
    case 'smartBtn_payOnlineBalance':
    case 'smartBtn_bookAppointment':
    case 'smartBtn_scheduleCleaning':
    case 'smartBtn_emergencyCall':
    case 'smartBtn_claimOffer':
    case 'smartBtn_activateInvisalign':
    case 'smartBtn_completeIntakeForm':
    case 'smartBtn_leaveReview':
    case 'smartBtn_referFriend':
      return {
        style: { ...DEFAULT_BUTTON_STYLE },
      } as SmartButtonContent;
  }
};

export const createDefaultContent = (type: BasicBlockType): EmailBlockContent => {
  switch (type) {
    case 'headerText':
      return {
        html: '<h1>Your headline here</h1>',
        settings: { ...DEFAULT_TEXT_SETTINGS, headingLevel: 1, fontSize: HEADING_FONT_SIZES[1]! },
      };
    case 'fullWidthText':
      return {
        html: '<p>Write your message here. Keep it clear, concise, and compelling.</p>',
        settings: { ...DEFAULT_TEXT_SETTINGS },
      };
    case 'twoColumnText':
      return {
        leftHtml: '<p>Left column content</p>',
        leftSettings: { ...DEFAULT_TEXT_SETTINGS },
        rightHtml: '<p>Right column content</p>',
        rightSettings: { ...DEFAULT_TEXT_SETTINGS },
      };
    case 'textImage':
      return {
        layoutMode: 'fit' as TextImageLayout,
        textHtml: '<p>Your message alongside an image.</p>',
        textSettings: { ...DEFAULT_TEXT_SETTINGS },
        image: { src: '', alt: 'Image description', settings: { ...DEFAULT_IMAGE_SETTINGS } },
      };
    case 'imageText':
      return {
        layoutMode: 'fit' as TextImageLayout,
        image: { src: '', alt: 'Image description', settings: { ...DEFAULT_IMAGE_SETTINGS } },
        textHtml: '<p>Your message alongside an image.</p>',
        textSettings: { ...DEFAULT_TEXT_SETTINGS },
      };
    case 'fullWidthImage':
      return { src: '', alt: 'Banner image', settings: { ...DEFAULT_IMAGE_SETTINGS } };
    case 'twoColumnImages':
      return {
        layoutMode: 'fit' as TwoColImageLayout,
        leftImage: { src: '', alt: 'Left image', settings: { ...DEFAULT_IMAGE_SETTINGS } },
        rightImage: { src: '', alt: 'Right image', settings: { ...DEFAULT_IMAGE_SETTINGS } },
      };
    case 'normalButton':
      return { ...DEFAULT_NORMAL_BUTTON_CONTENT };
    case 'separator':
      return { ...DEFAULT_SEPARATOR_CONTENT };
  }
};

// ─── Store ────────────────────────────────────────────────────────────────────

interface EmailBuilderState {
  blocks: EmailBlock[];
  emailHeader: StructuralBlock | null;
  emailFooter: StructuralBlock | null;
  selectedBlockId: string | null;
  deviceMode: DeviceMode;
  isFileManagerOpen: boolean;
  fileManagerTargetField: { blockId: string; field: string } | null;
  savedEmails: SavedEmailMeta[];

  /** Active email campaign metadata (set after creation dialog) */
  emailId: string | null;
  emailName: string;
  emailDescription: string;
  emailTags: string[];
  isSetupDialogOpen: boolean;

  /**
   * Template data to seed the canvas when the user picks a gallery template.
   * Cleared once EmailBuilderPage has applied it.
   */
  pendingTemplateData: {
    blocks: EmailBlock[];
    templateSettings: Partial<Record<string, boolean>>;
    /** Raw header structural block saved inside the template, if present */
    templateHeader: StructuralBlock | null;
    /** Raw footer structural block saved inside the template, if present */
    templateFooter: StructuralBlock | null;
    /** Raw theme saved inside the template, if present */
    templateTheme: {
      mode?: string;
      colors?: Record<string, string>;
      shape?: string;
      selectedPaletteId?: string | null;
    } | null;
  } | null;

  /** True when the canvas has unsaved changes since the last save */
  isDirty: boolean;

  /** Advanced CSS mode */
  advancedCssEnabled: boolean;
  advancedCssCode: string;

  /** GIF maker modal state */
  isGifMakerOpen: boolean;
  gifMakerBlockId: string | null;
  /** When the file manager is opened from the GIF maker, this is 'before' or 'after' */
  gifMakerImageTarget: 'before' | 'after' | null;
  /** Callback set by GifMakerModal so FileManager can route the URL back */
  onGifMakerImageSelected: ((url: string) => void) | null;

  setEmailMeta: (meta: EmailSetupMeta & { id: string }) => void;
  openSetupDialog: () => void;
  closeSetupDialog: () => void;
  resetEmail: () => void;
  setPendingTemplateData: (data: {
    blocks: EmailBlock[];
    templateSettings: Partial<Record<string, boolean>>;
    templateHeader: StructuralBlock | null;
    templateFooter: StructuralBlock | null;
    templateTheme: { mode?: string; colors?: Record<string, string>; shape?: string; selectedPaletteId?: string | null } | null;
  } | null) => void;
  markDirty: () => void;
  markClean: () => void;

  setBlocks: (blocks: EmailBlock[]) => void;
  addBlock: (type: BasicBlockType | SmartBlockType, atIndex?: number) => void;
  addSmartBlock: (type: SmartBlockType, atIndex?: number) => void;
  duplicateBlock: (id: string) => void;
  removeBlock: (id: string) => void;
  addEmailHeader: () => void;
  removeEmailHeader: () => void;
  updateEmailHeaderContent: (patch: Partial<EmailHeaderContent>) => void;
  /** Replace the entire header structural block (used when seeding from a template) */
  setEmailHeader: (block: StructuralBlock) => void;
  addEmailFooter: () => void;
  removeEmailFooter: () => void;
  updateEmailFooterContent: (patch: Partial<EmailFooterContent>) => void;
  /** Replace the entire footer structural block (used when seeding from a template) */
  setEmailFooter: (block: StructuralBlock) => void;
  selectBlock: (id: string | null) => void;
  updateBlockContent: (id: string, content: Partial<EmailBlockContent>) => void;
  setDeviceMode: (mode: DeviceMode) => void;
  openFileManager: (target?: { blockId: string; field: string }) => void;
  closeFileManager: () => void;
  insertImageFromFileManager: (url: string) => void;
  addSavedEmail: (meta: SavedEmailMeta) => void;
  saveAsDraft: (name: string, html: string) => void;

  openGifMaker: (blockId: string) => void;
  closeGifMaker: () => void;
  openFileManagerForGifMaker: (target: 'before' | 'after', onSelected: (url: string) => void) => void;
  /** Open the file manager with a plain callback — no block/field targeting needed */
  openFileManagerForCallback: (onSelected: (url: string) => void) => void;

  setAdvancedCssEnabled: (enabled: boolean) => void;
  setAdvancedCssCode: (code: string) => void;
  updateBlockCustomClass: (id: string, customClass: string) => void;
}

export const useEmailBuilderStore = create<EmailBuilderState>((set, get) => ({
  blocks: [],
  emailHeader: null,
  emailFooter: null,
  selectedBlockId: null,
  deviceMode: 'desktop',
  isFileManagerOpen: false,
  fileManagerTargetField: null,
  savedEmails: [],
  isGifMakerOpen: false,
  gifMakerBlockId: null,
  gifMakerImageTarget: null,
  onGifMakerImageSelected: null,

  emailId: null,
  emailName: '',
  emailDescription: '',
  emailTags: [],
  isSetupDialogOpen: false,
  isDirty: false,
  pendingTemplateData: null,

  advancedCssEnabled: false,
  advancedCssCode: '',

  setEmailMeta: ({ id, name, description, tags }) =>
    set({ emailId: id, emailName: name, emailDescription: description, emailTags: tags, isSetupDialogOpen: false }),

  openSetupDialog: () => set({ isSetupDialogOpen: true }),

  closeSetupDialog: () => set({ isSetupDialogOpen: false }),

  setPendingTemplateData: (data) => set({ pendingTemplateData: data }),

  markDirty: () => set({ isDirty: true }),

  markClean: () => set({ isDirty: false }),

  resetEmail: () =>
    set({
      blocks: [],
      emailHeader: null,
      emailFooter: null,
      selectedBlockId: null,
      emailId: null,
      emailName: '',
      emailDescription: '',
      emailTags: [],
      isSetupDialogOpen: false,
      isDirty: false,
      advancedCssEnabled: false,
      advancedCssCode: '',
      pendingTemplateData: null,
    }),

  setBlocks: (blocks) => set({ blocks, isDirty: true }),

  addBlock: (type, atIndex) =>
    set((state) => {
      const isSmartType = [
        'beforeAfterSlider', 'experienceInsurance', 'financingOptions',
        'serviceSpotlight', 'testimonial', 'video',
        'smartBtn_viewTreatmentPlan', 'smartBtn_checkInsuranceEligibility', 'smartBtn_payOnlineBalance',
        'smartBtn_bookAppointment', 'smartBtn_scheduleCleaning', 'smartBtn_emergencyCall',
        'smartBtn_claimOffer', 'smartBtn_activateInvisalign', 'smartBtn_completeIntakeForm',
        'smartBtn_leaveReview', 'smartBtn_referFriend',
      ].includes(type);
      const block: EmailBlock = {
        id: createId(),
        type,
        content: isSmartType
          ? createDefaultSmartContent(type as SmartBlockType)
          : createDefaultContent(type as BasicBlockType),
      };
      const blocks = [...state.blocks];
      if (atIndex !== undefined) {
        blocks.splice(atIndex, 0, block);
      } else {
        blocks.push(block);
      }
      return { blocks, selectedBlockId: block.id, isDirty: true };
    }),

  addSmartBlock: (type, atIndex) =>
    set((state) => {
      const block: EmailBlock = {
        id: createId(),
        type,
        content: createDefaultSmartContent(type),
      };
      const blocks = [...state.blocks];
      if (atIndex !== undefined) {
        blocks.splice(atIndex, 0, block);
      } else {
        blocks.push(block);
      }
      return { blocks, selectedBlockId: block.id, isDirty: true };
    }),

  duplicateBlock: (id) =>
    set((state) => {
      const index = state.blocks.findIndex((b) => b.id === id);
      if (index === -1) return state;
      const copy: EmailBlock = { ...state.blocks[index]!, id: createId() };
      const blocks = [...state.blocks];
      blocks.splice(index + 1, 0, copy);
      return { blocks, selectedBlockId: copy.id, isDirty: true };
    }),

  removeBlock: (id) =>
    set((state) => ({
      blocks: state.blocks.filter((b) => b.id !== id),
      selectedBlockId: state.selectedBlockId === id ? null : state.selectedBlockId,
      isDirty: true,
    })),

  addEmailHeader: () =>
    set((state) => {
      if (state.emailHeader) return state;
      return {
        emailHeader: {
          id: createId(),
          type: 'emailHeader',
          headerContent: { ...DEFAULT_HEADER_CONTENT },
        },
        isDirty: true,
      };
    }),

  removeEmailHeader: () =>
    set((state) => ({
      emailHeader: null,
      selectedBlockId: state.selectedBlockId === state.emailHeader?.id ? null : state.selectedBlockId,
      isDirty: true,
    })),

  updateEmailHeaderContent: (patch) =>
    set((state) => {
      if (!state.emailHeader) return state;
      return {
        emailHeader: {
          ...state.emailHeader,
          headerContent: {
            ...(state.emailHeader.headerContent ?? DEFAULT_HEADER_CONTENT),
            ...patch,
          },
        },
        isDirty: true,
      };
    }),

  setEmailHeader: (block) => set({ emailHeader: block, isDirty: true }),

  addEmailFooter: () =>
    set((state) => {
      if (state.emailFooter) return state;
      return {
        emailFooter: {
          id: createId(),
          type: 'emailFooter',
          footerContent: { ...DEFAULT_FOOTER_CONTENT, leftItems: [...DEFAULT_FOOTER_CONTENT.leftItems] as EmailFooterContent['leftItems'], rightItems: [...DEFAULT_FOOTER_CONTENT.rightItems] as EmailFooterContent['rightItems'] },
        },
        isDirty: true,
      };
    }),

  removeEmailFooter: () =>
    set((state) => ({
      emailFooter: null,
      selectedBlockId: state.selectedBlockId === state.emailFooter?.id ? null : state.selectedBlockId,
      isDirty: true,
    })),

  updateEmailFooterContent: (patch) =>
    set((state) => {
      if (!state.emailFooter) return state;
      return {
        emailFooter: {
          ...state.emailFooter,
          footerContent: {
            ...(state.emailFooter.footerContent ?? DEFAULT_FOOTER_CONTENT),
            ...patch,
          },
        },
        isDirty: true,
      };
    }),

  setEmailFooter: (block) => set({ emailFooter: block, isDirty: true }),

  selectBlock: (id) => set({ selectedBlockId: id }),

  updateBlockContent: (id, content) =>
    set((state) => ({
      blocks: state.blocks.map((block) =>
        block.id === id
          ? { ...block, content: { ...block.content, ...content } as EmailBlockContent }
          : block,
      ),
      isDirty: true,
    })),

  setDeviceMode: (mode) => set({ deviceMode: mode }),

  openFileManager: (target) =>
    set({ isFileManagerOpen: true, fileManagerTargetField: target ?? null }),

  closeFileManager: () =>
    set({ isFileManagerOpen: false, fileManagerTargetField: null, gifMakerImageTarget: null, onGifMakerImageSelected: null }),

  insertImageFromFileManager: (url) => {
    const { fileManagerTargetField, blocks, emailHeader, updateBlockContent } = get();
    if (!fileManagerTargetField) return;
    const { blockId, field } = fileManagerTargetField;

    // Header logo
    if (emailHeader && blockId === emailHeader.id && field === 'logoUrl') {
      set((state) => ({
        emailHeader: state.emailHeader
          ? {
              ...state.emailHeader,
              headerContent: {
                ...(state.emailHeader.headerContent ?? DEFAULT_HEADER_CONTENT),
                logoUrl: url,
              },
            }
          : null,
        isFileManagerOpen: false,
        fileManagerTargetField: null,
      }));
      return;
    }

    const block = blocks.find((b) => b.id === blockId);
    if (!block) return;
    const content = block.content as any;
    if (field === 'src') {
      updateBlockContent(blockId, { src: url } as any);
    } else if (field === 'image') {
      updateBlockContent(blockId, { image: typeof content.image === 'object' ? { ...content.image, src: url } : url } as any);
    } else if (field === 'leftImage') {
      updateBlockContent(blockId, { leftImage: { ...content.leftImage, src: url } } as any);
    } else if (field === 'rightImage') {
      updateBlockContent(blockId, { rightImage: { ...content.rightImage, src: url } } as any);
    } else if (field === 'beforeImage') {
      updateBlockContent(blockId, { beforeImage: url } as any);
    } else if (field === 'afterImage') {
      updateBlockContent(blockId, { afterImage: url } as any);
    } else if (field === 'videoUrl') {
      updateBlockContent(blockId, { videoUrl: url } as any);
    } else if (field === 'posterUrl') {
      updateBlockContent(blockId, { posterUrl: url } as any);
    }
    set({ isFileManagerOpen: false, fileManagerTargetField: null });
  },

  addSavedEmail: (meta) =>
    set((state) => ({ savedEmails: [meta, ...state.savedEmails] })),

  saveAsDraft: (name, html) =>
    set((state) => ({
      savedEmails: [
        { id: createId(), name, createdAt: new Date().toISOString(), html, status: 'draft' },
        ...state.savedEmails,
      ],
    })),

  openGifMaker: (blockId) => set({ isGifMakerOpen: true, gifMakerBlockId: blockId }),
  closeGifMaker: () => set({ isGifMakerOpen: false, gifMakerBlockId: null }),

  openFileManagerForGifMaker: (target, onSelected) =>
    set({ isFileManagerOpen: true, fileManagerTargetField: null, gifMakerImageTarget: target, onGifMakerImageSelected: onSelected }),

  openFileManagerForCallback: (onSelected) =>
    set({ isFileManagerOpen: true, fileManagerTargetField: null, gifMakerImageTarget: null, onGifMakerImageSelected: onSelected }),

  setAdvancedCssEnabled: (enabled) => set({ advancedCssEnabled: enabled }),

  setAdvancedCssCode: (code) => set({ advancedCssCode: code }),

  updateBlockCustomClass: (id, customClass) =>
    set((state) => ({
      blocks: state.blocks.map((b) => b.id === id ? { ...b, customClass } : b),
      isDirty: true,
    })),
}));
